import SelectTemplate from '../../html/components/select.html'
import SelectToggleTemplate from '../../html/components/select-toggle.html'
import SelectMenuTemplate from '../../html/components/select-menu.html'
import SelectOptionTemplate from '../../html/components/select-option.html'

import {inert} from '../shared/directives'
import Component from '../shared/component'
import Relation, {HasOne, HasMany, MemberOf} from '../shared/mixins/relation'
import {PopperMixin, PopperToggleMixin} from '../shared/mixins/popper'

import {FormControlMixin} from './form'

const selectToggle = Relation()
const selectMenu = Relation()
const selectOptions = Relation()

export const Select = Component('v-select', {
  mixins: [
    HasOne(selectToggle, 'toggle'),
    HasOne(selectMenu, 'menu'),
    HasMany(selectOptions, 'options'),
    PopperToggleMixin('menu'),
    FormControlMixin('select')
  ],
  template: SelectTemplate,
  props: {
    name: {
      type: String,
      default: null
    },
    value: {
      default: null
    }
  },
  data () {
    return {
      focused: this.index
    }
  },
  computed: {
    index () {
      return this.options.findIndex(option => this.active === option.value)
    },

    open () {
      const {toggle} = this

      if (toggle) {
        return toggle.open
      }
    }
  },
  watch: {
    active () {
      this.focused = this.index

      this.options.forEach(option => {
        if (this.active === option.value) {
          option.select()
        } else {
          option.deselect()
        }
      })
    },

    options () {
      this.options.forEach(option => {
        if (this.active === null && option.active) {
          this.select(option.value)
        }

        if (this.active === option.value) {
          option.select()
        } else {
          option.deselect()
        }

        option.$on('change', active => active
          ? this.select(option.value)
          : null
        )
      })
    },

    open () {
      if (this.open) {
        this.pop()

        const option = this.options[this.index]

        if (option) {
          option.$el.focus()
        }
      } else {
        this.unpop()
      }
    },

    focused () {
      if (this.open) {
        const option = this.options[this.focused]

        if (option) {
          option.$el.focus()
        }
      }
    }
  },
  methods: {
    select (value) {
      this.active = value

      const {toggle} = this

      if (toggle && toggle.open) {
        toggle.$el.focus()
      }

      this.hide()
    },

    show () {
      const {toggle} = this

      if (toggle) {
        toggle.show()
      }
    },

    hide () {
      const {toggle} = this

      if (toggle) {
        toggle.hide()
      }
    },

    clear () {
      this.select(null)

      const {toggle} = this

      if (toggle) {
        toggle.$el.focus()
      }
    }
  }
})

export const SelectToggle = Component('v-select-toggle', {
  mixins: [
    MemberOf(selectToggle, 'select')
  ],
  template: SelectToggleTemplate,
  props: {
    variant: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      open: false
    }
  },
  computed: {
    active () {
      const {select} = this

      if (!select) {
        return
      }

      const {options, index} = select

      const active = options[index]

      if (active) {
        return active.title
      }
    },

    target () {
      const {select} = this

      if (!select) {
        return
      }

      const {menu} = select

      if (menu) {
        return menu.id
      }
    },

    label () {
      const {select} = this

      if (select) {
        return select.label
      }
    }
  },
  watch: {
    open () {
      this.$emit('toggle', this.open)
    }
  },
  methods: {
    toggle () {
      if (this.open) {
        this.hide()
      } else {
        this.show()
      }
    },

    show () {
      if (!this.open) {
        this.open = true
      }
    },

    hide () {
      if (this.open) {
        this.open = false
      }
    }
  }
})

export const SelectMenu = Component('v-select-menu', {
  mixins: [
    MemberOf(selectMenu, 'select'),
    PopperMixin('select', {
      placement: 'bottom',
      shift: 'start'
    })
  ],
  directives: {
    inert
  },
  template: SelectMenuTemplate,
  props: {
    labelSearch: {
      type: String,
      required: true
    }
  },
  computed: {
    options () {
      const {select} = this

      return select ? select.options : []
    },

    active () {
      const {select} = this

      if (!select) {
        return
      }

      const {active} = select

      if (active) {
        return active.id
      }
    },

    open () {
      const {select} = this

      if (!select) {
        return
      }

      const {toggle} = select

      if (toggle) {
        return toggle.open
      }
    }
  },
  methods: {
    tab (event) {
      let prevent

      if (event.shiftKey) {
        prevent = this.prev()
      } else {
        prevent = this.next()
      }

      if (prevent) {
        event.preventDefault()
      }
    },

    next () {
      const {select} = this

      if (select && select.focused < this.options.length - 1) {
        select.focused++
        return true
      }

      return false
    },

    prev () {
      const {select} = this

      if (select && select.focused > 0) {
        select.focused--
        return true
      }

      return false
    }
  }
})

export const SelectOption = Component('v-select-option', {
  mixins: [
    MemberOf(selectOptions)
  ],
  template: SelectOptionTemplate,
  props: {
    value: {
      default: null
    },
    title: {
      type: String,
      required: true
    },
    selected: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      active: this.selected
    }
  },
  watch: {
    selected () {
      this.active = this.selected
    },

    active () {
      this.$emit('change', this.active)
    }
  },
  methods: {
    select () {
      if (!this.active) {
        this.active = true
      }
    },

    deselect () {
      if (this.active) {
        this.active = false
      }
    }
  }
})
